import api from 'utils/api'

import { handleError } from '../utils'

// AI House session: one persistent, resumable draft chain per (source asset, user).
const FETCH_REQUEST = 'contentexpress/aiHouse/FETCH_REQUEST'
const FETCH_SUCCESS = 'contentexpress/aiHouse/FETCH_SUCCESS'
const FETCH_FAILURE = 'contentexpress/aiHouse/FETCH_FAILURE'

const RECEIVE_TURNS = 'contentexpress/aiHouse/RECEIVE_TURNS' // silent poll update (no loading toggle)

const CREATE_REQUEST = 'contentexpress/aiHouse/CREATE_REQUEST'
const CREATE_SUCCESS = 'contentexpress/aiHouse/CREATE_SUCCESS'
const CREATE_FAILURE = 'contentexpress/aiHouse/CREATE_FAILURE'

const SAVE_SUCCESS = 'contentexpress/aiHouse/SAVE_SUCCESS' // stamp resultAssetId on the saved turn

const DELETE_SUCCESS = 'contentexpress/aiHouse/DELETE_SUCCESS' // optimistic tombstone of a deleted draft image

const CLEAR_REQUEST = 'contentexpress/aiHouse/CLEAR_REQUEST' // wipe the chain — bumps epoch so a stale in-flight poll read is dropped

// Initial State
const initialState = {
  errors: [],
  turns: [], // one row per turn, ordered by sequence
  deletedIds: [], // versionIds tombstoned this session; re-applied on every read so a stale re-GET can't resurrect them
  loaded: false,
  loading: false,
  submitting: false,
  // Bumped on every clear. A poll read started before the clear carries the old epoch; the reducer drops it
  // so an in-flight GET can't repaint the wiped conversation (deletedIds can't guard a whole-chain wipe).
  epoch: 0,
}

// Actions
export function fetchRequest() {
  return { type: FETCH_REQUEST }
}

export function fetchSuccess(turns = []) {
  return { type: FETCH_SUCCESS, turns }
}

export function fetchFailure(errors = []) {
  return { type: FETCH_FAILURE, errors }
}

export function receiveTurns(turns = [], epoch) {
  return { type: RECEIVE_TURNS, turns, epoch }
}

export function clearRequest() {
  return { type: CLEAR_REQUEST }
}

export function createRequest() {
  return { type: CREATE_REQUEST }
}

export function createSuccess(turn) {
  return { type: CREATE_SUCCESS, turn }
}

export function createFailure(errors = []) {
  return { type: CREATE_FAILURE, errors }
}

export function saveSuccess(versionId, resultAssetId) {
  return { type: SAVE_SUCCESS, versionId, resultAssetId }
}

export function deleteSuccess(versionId) {
  return { type: DELETE_SUCCESS, versionId }
}

// Action Creators

// Load / resume the session for a source asset (also the initial poll read).
export function loadSession(sourceAssetId) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/v2/assets/${sourceAssetId}/ai-versions`)
      .then(({ data }) => {
        dispatch(fetchSuccess(data || []))

        return { success: true, data }
      })
      .catch((error) => {
        const errors = handleError(error)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Silent re-read used by the adaptive poll loop — same endpoint, no loading flags.
export function refreshSession(sourceAssetId) {
  return (dispatch, getState) => {
    // Stamp the read with the epoch it started under; a clear mid-flight bumps it so the reducer drops this response.
    const { epoch } = getState().aiHouse
    const promise = api(`/v2/assets/${sourceAssetId}/ai-versions`)
      .then(({ data }) => {
        dispatch(receiveTurns(data || [], epoch))

        return { success: true, data }
      })
      .catch((error) => {
        const errors = handleError(error)

        return { success: false, errors }
      })

    return promise
  }
}

// Start a generation turn. `body` = { prompt, promptKey?, customPrompt?, fromVersionId? }
export function createTurn(sourceAssetId, body) {
  const config = { method: 'POST', data: body }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/v2/assets/${sourceAssetId}/ai-versions`, {}, config)
      .then(({ data }) => {
        // POST returns `versionId`; normalise to the GET's turn shape (`id`) for an optimistic tile.
        // promptKey/customPrompt aren't echoed back, so carry them from the request for display.
        const turn = {
          id: data.versionId,
          requestId: data.requestId,
          status: data.status,
          sequence: data.sequence,
          parentVersionId: data.parentVersionId,
          sourceAssetId: data.sourceAssetId,
          rootSourceAssetId: data.rootSourceAssetId,
          serviceJobId: data.serviceJobId,
          sourceUrl: data.sourceUrl,
          promptKey: body.promptKey || null,
          customPrompt: body.customPrompt || null,
          outputUrl: null,
          error: null,
        }
        dispatch(createSuccess(turn))

        return { success: true, data: turn }
      })
      .catch((error) => {
        const errors = handleError(error)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Save a completed draft as a ClientGenerated gallery asset ("AI Edits").
// Idempotent per draft, but read-then-write — the caller must await this before re-enabling
// Save, so two concurrent saves can't mint two gallery assets. Returns { resultAssetId }.
export function saveTurn(versionId) {
  const config = { method: 'POST' }

  return (dispatch) => {
    const promise = api(`/v2/ai-versions/${versionId}/save`, {}, config)
      .then(({ data }) => {
        dispatch(saveSuccess(versionId, data.resultAssetId))

        return { success: true, data }
      })
      .catch((error) => {
        const errors = handleError(error)

        return { success: false, errors }
      })

    return promise
  }
}

// Delete a draft's image. The turn stays as a tombstone (outputUrl null, deletedAt set); a saved
// asset made from it is untouched. Optimistic tombstone, then re-GET since the read is the source of truth.
export function deleteTurn(sourceAssetId, versionId) {
  const config = { method: 'DELETE' }

  return (dispatch) => {
    const promise = api(`/v2/ai-versions/${versionId}`, {}, config)
      .then(({ data }) => {
        dispatch(deleteSuccess(versionId))
        if (sourceAssetId) dispatch(refreshSession(sourceAssetId))

        return { success: true, data }
      })
      .catch((error) => {
        const errors = handleError(error)

        return { success: false, errors }
      })

    return promise
  }
}

// New session: clears unsaved drafts server-side, then re-GETs the fresh chain. Idempotent.
export function clearSession(sourceAssetId) {
  const config = { method: 'POST' }

  return (dispatch) => {
    dispatch(clearRequest()) // bump epoch now so a poll read already in flight is dropped when it lands
    const promise = api(`/v2/assets/${sourceAssetId}/ai-versions/clear`, {}, config)
      .then(({ data }) => {
        dispatch(loadSession(sourceAssetId))

        return { success: true, data }
      })
      .catch((error) => {
        const errors = handleError(error)

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, turns, turn, errors, versionId, resultAssetId, epoch } = action

  switch (type) {
    case CLEAR_REQUEST:
      // Invalidate any poll read already in flight; the fresh loadSession that follows repaints the chain.
      return { ...state, epoch: state.epoch + 1 }
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loaded: false,
        errors: [],
        turns: [],
        deletedIds: [], // fresh session — drop tombstones from the previous source asset
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        turns: turns || [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors,
      }
    case RECEIVE_TURNS: {
      // A read issued before a clear carries a stale epoch — drop it so it can't repaint the wiped chain.
      if (epoch !== state.epoch) return state
      // Blind reads (poll / post-delete re-GET) can hit a replica that hasn't observed a delete yet and
      // return the turn un-deleted. Re-apply any local tombstone so the "deleted" state can't flash back.
      const incoming = turns || []
      if (!state.deletedIds.length) return { ...state, turns: incoming }

      return {
        ...state,
        turns: incoming.map((t) =>
          state.deletedIds.includes(t.id) && !t.deletedAt
            ? { ...t, outputUrl: null, outputFileId: null, deletedAt: true }
            : t
        ),
      }
    }
    case CREATE_REQUEST:
      return {
        ...state,
        submitting: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        submitting: false,
        errors: [],
        turns: [...state.turns, turn],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        submitting: false,
        errors,
      }
    case SAVE_SUCCESS:
      return {
        ...state,
        turns: state.turns.map((t) => (t.id === versionId ? { ...t, resultAssetId } : t)),
      }
    case DELETE_SUCCESS:
      // Optimistic tombstone: drop the image so the "deleted" state paints immediately. Record the id so
      // RECEIVE_TURNS keeps it tombstoned even if the follow-up re-GET (or an in-flight poll) still returns
      // the image; the authoritative deletedAt timestamp overwrites the sentinel once the read reflects it.
      return {
        ...state,
        deletedIds: state.deletedIds.includes(versionId) ? state.deletedIds : [...state.deletedIds, versionId],
        turns: state.turns.map((t) =>
          t.id === versionId ? { ...t, outputUrl: null, outputFileId: null, deletedAt: t.deletedAt || true } : t
        ),
      }
    default:
      return state
  }
}
